// @flow
import React, {Component, Fragment} from 'react';
import {compose} from 'redux';
import {connect} from 'react-redux';
import {FormattedMessage} from 'react-intl';
import {injectIntl, type IntlShape} from 'react-intl';
import classNames from 'classnames';
import {pick} from 'lodash';
import {
    syncPets,
    SYNC_STRATEGY_PET_INCLUDED
} from '@homeaway/search-url-parser';
import Experiment from '@homeaway/react-experiment';
import Typeahead from '@homeaway/react-destination-typeahead';
import {Button} from '@homeaway/react-buttons';
import {SVGIcon} from '@homeaway/react-svg';
import {GUEST_MODAL, DATE_MODAL} from '../../actions/modals';
import {isDifferent} from '../../utils';
import {isFilterSelected, parseFilterName} from '../../utils/filters';
import {media, XL_MIN} from '../../utils/breakpoints';
import ConnectedDateRangePicker from '../ConnectedDateRangePicker/ConnectedDateRangePicker';
import ConnectedGuestPicker from '../ConnectedGuestPicker/ConnectedGuestPicker';
import dateRangePickerActions from '../../actions/dateRangePicker';
import searchParamsActions from '../../actions/searchParams';
import routerActions from '../../actions/router';
import isBoundingBoxSearch from '../../utils/isBoundingBoxSearch';
import {HA_SERP_DATES_PROMPT} from '../../abacus/experiments';
import DatesPromptTooltip from '../DatesPromptTooltip/DatesPromptTooltip';

import type {Element} from 'react';
import {type SearchParamsType} from '../../reducers/searchParams';
import type {State as ReduxState} from '../../reducers';
import type {Breakpoint} from '../../utils/breakpoints';

import './SearchFields.less';

type DispatchProps = {
    onOpenDateRangePicker: () => void,
    onCloseDateRangePicker: () => void,
    onOpenGuestPicker: () => void,
    onCloseGuestPicker: () => void,
    setDateRangePickerDates: typeof dateRangePickerActions.setDateRangePickerDates,
    updateFilterCheck: typeof searchParamsActions.updateFilterCheck,
    setFilterFromTypeahead: typeof searchParamsActions.setFilterFromTypeahead
};

type StateProps = {
    adultsCount: number,
    arrival: string,
    breakpoint: Breakpoint,
    childrenCount: number,
    dateRangePickerIsOpen: boolean,
    departure: string,
    hasOpenedDatesPicker: boolean,
    hasOpenedGuestsPicker: boolean,
    shouldShowDatesPrompt: boolean,
    guestPickerIsOpen: boolean,
    isBoundingBoxSearch: boolean,
    isMobile: boolean,
    isRegionSearch: boolean,
    isPromptingForDates: boolean,
    isTouchDevice: boolean,
    petIncluded: boolean,
    filter: string[],
    pinnedPropertyListing: string,
    q: string,
    transDates: any
};

type PickerLabelProps = {
    textClass?: string,
    messageId: string | null,
    defaultText: string
};

export const PickerInputLabel = ({
    textClass = '',
    messageId,
    defaultText
}: PickerLabelProps) => {
    const message = messageId ? (
        <FormattedMessage id={messageId} defaultMessage={defaultText} />
    ) : (
        defaultText
    );

    return <span className={textClass}>{message}</span>;
};

type MessageDescriptor = {messageId: string, defaultText: string};

type SelectionData = {
    destination: string,
    destinationChanged: boolean,
    subDestination: any,
    subDestinationChanged: boolean,
    destinationUuid: string,
    destinationType: string
};

type OwnProps = {
    fieldClassName?: string,
    searchIcon?: Element<typeof SVGIcon> | boolean,
    showGuests?: boolean,
    searchButton?: Element<typeof Button>,
    datepickerConfig?: {
        positionIconRight?: boolean,
        dateTextDescriptorInput1?: MessageDescriptor,
        dateTextDescriptorInput2?: MessageDescriptor
    },
    guestpickerConfig?: {
        guestsIcon?: Element<typeof SVGIcon>,
        noGuestDescriptor?: MessageDescriptor,
        hasGuestDescriptor?: MessageDescriptor
    },
    typeaheadConfig?: {messages: any}
};

type Props = StateProps &
    DispatchProps & {
        intl: IntlShape,
        onChange: (SearchParamsType) => void,
        onLocationChange?: (SearchParamsType) => void
    } & OwnProps;

export type State = {
    +q?: string,
    +filter?: string[],
    +arrival?: string,
    +departure?: string,
    +adultsCount?: number,
    +childrenCount?: number,
    +petIncluded?: boolean,
    +pinnedPropertyListing?: string | null
};

export type ComponentState = {
    q: string,
    filter: string[],
    arrival: string,
    departure: string,
    adultsCount: number,
    childrenCount: number,
    initialDates: {
        arrival: string,
        departure: string
    },
    petIncluded: boolean,
    pinnedPropertyListing: string | null,
    filterSelected: boolean
};

export const searchFieldParameters = [
    'q',
    'arrival',
    'departure',
    'adultsCount',
    'childrenCount',
    'petIncluded',
    'pinnedPropertyListing',
    'filter'
];

export class SearchFields extends Component<Props, ComponentState> {
    static defaultProps = {
        searchIcon: false,
        showGuests: true
    };

    constructor(props: Props) {
        super(props);

        this.state = {
            ...pick(props, searchFieldParameters)
        };

        props.setDateRangePickerDates(
            this.useDefaultDateWhenNonExist(props.arrival),
            this.useDefaultDateWhenNonExist(props.departure)
        );
    }

    componentWillReceiveProps(nextProps: Props) {
        /**
         * We need to reset the initialValues when they change for the back button
         * to work
         */
        if (
            isDifferent(
                pick(nextProps, searchFieldParameters),
                pick(this.props, searchFieldParameters)
            )
        ) {
            this.setState({...pick(nextProps, searchFieldParameters)});
        }

        const nextDates = nextProps.transDates;
        if (
            !nextProps.dateRangePickerIsOpen &&
            this.props.dateRangePickerIsOpen
        ) {
            this.setState({
                arrival: this.useDefaultDateWhenNonExist(nextDates.arrival),
                departure: this.useDefaultDateWhenNonExist(nextDates.departure)
            });
        }
    }

    useDefaultDateWhenNonExist = (date: ?string): string => {
        if (date !== null && typeof date !== 'undefined') {
            return date;
        }
        return '';
    };

    handleDateRangeChange = (arrival: string, departure: string) => {
        this.setState({arrival, departure});
    };

    getSearchParams = (newState: State) => {
        let searchParams: SearchParamsType = {
            ...pick(this.state, searchFieldParameters),
            ...newState
        };
        Object.entries(searchParams).forEach(([param, value]) => {
            if (value === '') {
                searchParams[param] = null;
            }
        });
        return searchParams;
    };

    handleOnChange = (newState: State = {}) => {
        this.props.onChange(
            syncPets(this.getSearchParams(newState), SYNC_STRATEGY_PET_INCLUDED)
        );
    };

    updateParamsOnLocationChange = (selectionData: SelectionData) => {
        const newParams = {
            q: selectionData.destination,
            pinnedPropertyListing: null
        };

        this.setState(newParams);

        const onLocationChange =
            this.props.onLocationChange || this.props.onChange;

        const finalParams = this.getSearchParams(newParams);

        // without removal, values of these fields will override all other values of search params
        delete finalParams.filter;
        delete finalParams.petIncluded;

        onLocationChange(finalParams);
    };

    handleSelectDestination = (selectionData: SelectionData) => {
        const hasRegionAndLocation =
            this.props.isBoundingBoxSearch || this.props.isRegionSearch;

        if (
            !selectionData.subDestinationChanged &&
            !selectionData.destinationChanged &&
            !hasRegionAndLocation
        ) {
            return;
        }
        const {filter} = this.state;
        let typeaheadFilter = null;

        if (
            selectionData.subDestination &&
            selectionData.subDestinationChanged
        ) {
            typeaheadFilter = parseFilterName(
                selectionData.subDestination.filter.id
            );
        }

        if (typeaheadFilter && !isFilterSelected(filter, typeaheadFilter)) {
            this.props.setFilterFromTypeahead(typeaheadFilter);
            this.setState({filterSelected: true});
        }

        if (!this.hasDates()) {
            this.props.onOpenDateRangePicker();
        }

        this.updateParamsOnLocationChange(selectionData);
    };

    hasDates = () => {
        const {arrival, departure} = this.state;
        return !!(arrival && departure);
    };

    handleGuestSelectorChange = (key: string, value: any) => {
        this.setState({[key]: value});
    };

    handleGuestSelectorClose = () => {
        const {
            isMobile,
            onCloseGuestPicker,
            onOpenDateRangePicker
        } = this.props;

        this.handleOnChange();
        onCloseGuestPicker();

        if (!isMobile && !this.hasDates() && !this.props.hasOpenedDatesPicker) {
            onOpenDateRangePicker();
        }
    };

    handleDateRangeChangeSubmit = () => {
        const {arrival = '', departure = ''} = this.state;
        const {
            isMobile,
            onCloseDateRangePicker,
            onOpenGuestPicker,
            setDateRangePickerDates,
            transDates: {
                arrival: arrivalFromState,
                departure: departureFromState
            }
        } = this.props;

        if ((!!arrival && !!departure) || isMobile) {
            setDateRangePickerDates(arrival, departure);
        }

        onCloseDateRangePicker();

        if (
            !isMobile &&
            !this.hasGuest() &&
            !this.props.hasOpenedGuestsPicker
        ) {
            onOpenGuestPicker();
        }

        if (
            ((!!arrival && !!departure) || (!arrival && !departure)) &&
            (arrival !== arrivalFromState || departure !== departureFromState)
        ) {
            this.handleOnChange();
        }
    };

    handleRevertDateRangeChange = () => {
        const state = this.state;
        const {
            isMobile,
            onCloseDateRangePicker,
            transDates: {arrival, departure}
        } = this.props;
        if (isMobile && !state.arrival && !state.departure) {
            this.handleDateRangeChangeSubmit();
        } else {
            this.setState({arrival, departure});
        }

        onCloseDateRangePicker();
    };

    handleDateRangeCleared = () => {
        if (this.props.isMobile) {
            return;
        }
        this.props.setDateRangePickerDates('', '');
        this.handleOnChange({arrival: '', departure: ''});
    };

    hasGuest = () => {
        const {
            adultsCount = 0,
            childrenCount = 0,
            petIncluded = false
        } = this.state;
        return adultsCount > 0 || childrenCount > 0 || petIncluded;
    };

    handleDateRangeOpen = () => {
        this.props.onOpenDateRangePicker();
    };

    handleGuestPickerOpen = () => {
        this.props.onOpenGuestPicker();
    };

    render() {
        const {locale} = this.props.intl;
        const {
            breakpoint,
            datepickerConfig,
            dateRangePickerIsOpen,
            fieldClassName,
            guestpickerConfig,
            guestPickerIsOpen,
            isMobile,
            isPromptingForDates,
            searchButton,
            searchIcon,
            shouldShowDatesPrompt,
            showGuests,
            typeaheadConfig
        } = this.props;
        const {
            adultsCount,
            childrenCount,
            petIncluded,
            arrival,
            departure,
            q
        } = this.state;

        const defaultSearchText = q || '';
        const isDatedSearch = this.hasDates();
        const showDangerStyles =
            !isMobile && !isDatedSearch && !isPromptingForDates;

        const dateRangePickerProps = {
            danger: showDangerStyles,
            startDate: this.state.arrival,
            endDate: this.state.departure,
            onDateRangeChange: this.handleDateRangeChange,
            onFocus: this.handleDateRangeOpen,
            onOpen: this.handleDateRangeOpen,
            onDateRangeClear: this.handleDateRangeCleared,
            onCancel: this.handleRevertDateRangeChange,
            onSubmit: this.handleDateRangeChangeSubmit,
            isOpen: dateRangePickerIsOpen,
            ...datepickerConfig
        };

        const dateRangePicker = (
            <ConnectedDateRangePicker {...dateRangePickerProps} />
        );

        return (
            <div className="SearchFields">
                <div
                    className={classNames(
                        'SearchFields__field',
                        'SearchFields__field--first',
                        'SearchFields__field--typeahead',
                        fieldClassName
                    )}
                >
                    <Typeahead
                        className="Typeahead"
                        search={defaultSearchText}
                        onSearchSelected={this.handleSelectDestination}
                        locale={locale}
                        icon={searchIcon}
                        usePopoverAnimation={false}
                        useResultsAnimation={false}
                        hideSelectedFilter
                        {...typeaheadConfig}
                    />
                </div>
                <div
                    className={classNames(
                        'SearchFields__field',
                        fieldClassName,
                        {
                            'DateRange_hover-override': arrival && !departure,
                            'text-danger__border': showDangerStyles
                        }
                    )}
                >
                    <Experiment name={HA_SERP_DATES_PROMPT}>
                        {({Variant}) => {
                            return (
                                <Fragment>
                                    <Variant variant={[0, 1]}>
                                        <Fragment>{dateRangePicker}</Fragment>
                                    </Variant>
                                    <Variant variant={2}>
                                        {shouldShowDatesPrompt ? (
                                            <DatesPromptTooltip
                                                portalClassName={
                                                    'SearchFields__DateRangeTooltip'
                                                }
                                                dateRangePicker={
                                                    dateRangePicker
                                                }
                                                placement={
                                                    breakpoint === XL_MIN
                                                        ? 'top'
                                                        : 'bottom'
                                                }
                                            />
                                        ) : (
                                            <Fragment>
                                                {dateRangePicker}
                                            </Fragment>
                                        )}
                                    </Variant>
                                </Fragment>
                            );
                        }}
                    </Experiment>
                </div>
                {searchButton}
                {showGuests && (
                    <div
                        className={classNames(
                            'SearchFields__field',
                            'SearchFields__field--guest-picker',
                            'SearchFields__field--last',
                            fieldClassName
                        )}
                    >
                        <ConnectedGuestPicker
                            adultsCount={adultsCount}
                            childrenCount={childrenCount}
                            petIncluded={petIncluded}
                            onChange={this.handleGuestSelectorChange}
                            onClose={this.handleGuestSelectorClose}
                            onExit={this.handleGuestSelectorClose}
                            onOpen={this.handleGuestPickerOpen}
                            isOpen={guestPickerIsOpen}
                            isMobile={isMobile}
                            {...guestpickerConfig}
                        />
                    </div>
                )}
            </div>
        );
    }
}

const mapStateToProps = ({
    abacus,
    system: {directEntry},
    dateRangePicker,
    modals,
    searchParams: {params},
    searchTerm: {description: searchTerm},
    viewport: {breakpoint}
}: ReduxState): StateProps => {
    const isMobile = !media(breakpoint, {minWidth: 'SM_MIN'});
    return {
        breakpoint,
        hasOpenedGuestsPicker: modals.GUESTS.hasOpened,
        hasOpenedDatesPicker: modals.DATES.hasOpened,
        dateRangePickerIsOpen: modals.DATES.isOpen,
        shouldShowDatesPrompt:
            !(params.arrival && params.departure) &&
            !modals.DATES.hasOpened &&
            directEntry,
        guestPickerIsOpen: modals.GUESTS.isOpen,
        transDates: dateRangePicker,
        isMobile,
        isPromptingForDates: !!(
            abacus &&
            abacus[HA_SERP_DATES_PROMPT] &&
            abacus[HA_SERP_DATES_PROMPT].value
        ),
        ...pick(params, searchFieldParameters),
        // if searchTerm isn't provided, use the q provided in the URL
        q: searchTerm || '',
        isBoundingBoxSearch: isBoundingBoxSearch(params),
        isRegionSearch: !!params.regionId
    };
};

// NOTE: if these are not thunks, we need to dispatch
const mapDispatchToProps = {
    onOpenDateRangePicker: () => routerActions.pushModal(DATE_MODAL),
    onCloseDateRangePicker: () => routerActions.popModal(DATE_MODAL),
    onOpenGuestPicker: () => routerActions.pushModal(GUEST_MODAL),
    onCloseGuestPicker: () => routerActions.popModal(GUEST_MODAL),
    setDateRangePickerDates: dateRangePickerActions.setDateRangePickerDates,
    updateFilterCheck: searchParamsActions.updateFilterCheck,
    setFilterFromTypeahead: searchParamsActions.setFilterFromTypeahead
};

export default compose(
    connect(
        mapStateToProps,
        mapDispatchToProps
    ),
    injectIntl
)(SearchFields);
